import { ComponentsOverrides, createTheme, ThemeOptions } from '@mui/material/styles';
import { DateRangeCalendarClassKey, DateRangePickerProps } from '@mui/x-date-pickers-pro';
import { deDE } from '@mui/x-date-pickers/locales';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import { DateTime } from 'luxon';

declare module '@mui/material/styles' {
  interface Theme {
    status: {
      danger: string;
    };
  }

  export interface ComponentNameToClassKey {
    MuiDateRangePicker: DateRangeCalendarClassKey;
  }

  interface Components<Theme = unknown> {
    MuiDateRangePicker?: {
      defaultProps?: Partial<DateRangePickerProps<DateTime>>;
      styleOverrides?: ComponentsOverrides<Theme>['MuiDateRangePicker'];
    };
  }

  interface TypographyVariants {
    body3: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
  }
  interface Palette {
    rose: Record<string, string>;
    blue: Record<string, string>;
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    status?: {
      danger?: string;
    };
  }

  interface PaletteOptions {
    rose?: Record<string, string>;
    blue?: Record<string, string>;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
  }
}

const spacing = 4;

const colors = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  blue: {
    50: '#DEF3FB',
    200: '#80CFF1',
    500: '#009FE3',
    600: '#038CC7',
    800: '#0278AB',
  },
  rose: {
    50: '#F7EDF3',
    200: '#EB9DCB',
    500: '#BB448B',
    600: '#852E62',
    800: '#5E0A3C',
  },
  purple: {
    50: '#EEE4F0',
    200: '#B39DDB',
    500: '#673AB7',
    600: '#5E35B1',
    800: '#4527A0',
  },
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
  },
  success: {
    main: '#98DAD9',
    light: '#CBEDEC',
    dark: '#169C99',
  },
  error: {
    main: '#D32F2F',
    light: '#FDEDED',
    dark: '#DC3545',
  },
  warning: {
    main: '#FCBC87',
    light: '#FFF4E5',
    dark: '#EF6C00',
  },
};

export const themeConfig: ThemeOptions = {
  spacing,
  shape: {
    borderRadius: 12,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 994,
      lg: 1280,
      xl: 1480,
    },
  },
  palette: {
    primary: {
      main: colors.rose[500],
      light: colors.rose[50],
      dark: colors.rose[800],
    },
    secondary: {
      main: colors.purple[500],
      light: colors.purple[50],
      dark: colors.purple[800],
    },
    text: {
      primary: colors.grey[800],
      secondary: colors.grey[500],
    },
    error: {
      main: colors.error.main,
      light: colors.error.light,
      dark: colors.error.dark,
    },
    warning: {
      main: colors.warning.main,
      light: colors.warning.light,
      dark: colors.warning.dark,
    },
    success: {
      main: colors.success.main,
      light: colors.success.light,
      dark: colors.success.dark,
    },
    grey: colors.grey,
    rose: colors.rose,
    blue: colors.blue,
  },
  typography: {
    fontSize: 14,
    fontFamily: ['Lato', 'Arial', 'sans-serif'].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '34px',
      lineHeight: '42px',
    },
    h2: {
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '32px',
    },
    h3: {
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '38px',
    },
    h4: {
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '24px',
    },
    h5: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '24px',
    },
    h6: {
      fontWeight: 700,
      lineHeight: `${spacing * 3}px`,
    },
    subtitle1: {
      fontWeight: 400,
      fontSize: '20px',
      lineHeight: '28px',
    },
    body1: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
    },
    body2: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '21px',
    },
    body3: {
      fontFamily: ['Lato', 'Arial', 'sans-serif'].join(', '),
      fontWeight: 200,
      fontSize: '12px',
      lineHeight: '18px',
    },
    caption: {
      fontWeight: 400,
      fontSize: '10px',
      lineHeight: '16px',
    },
  },
  components: {
    MuiStepLabel: {
      styleOverrides: {
        labelContainer: {
          color: colors.grey[800],
          '.Mui-active, .Mui-completed': {
            color: `${colors.rose[500]} !important`,
          },
          '.MuiStepLabel-alternativeLabel': {
            marginTop: '2px !important',
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          minWidth: `${spacing * 6}px`,
          minHeight: `${spacing * 6}px`,
          height: `${spacing * 6}px`,
          color: colors.rose[500],
          fontSize: spacing * 3,
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          fontSize: 16,
          color: colors.grey[800],
        },
        text: {
          fontWeight: 900,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: `${spacing * 3}px ${spacing * 4}px`,
          borderRadius: 4,
        },
        text: {
          padding: 4,
        },
        sizeMedium: {
          height: spacing * 11,
        },
        sizeSmall: {
          height: spacing * 7,
          padding: `${spacing}px ${spacing * 2}px`,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: spacing * 3,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          top: 1,
        },
        outlined: {
          color: colors.grey[700],
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: colors.grey[50],
        },
        notchedOutline: {
          borderColor: colors.grey[500],
        },
        inputSizeSmall: {
          height: spacing * 5,
          padding: `${spacing * 3}px ${spacing * 4}px`,
          fontSize: spacing * 3.5,
        },
        inputMultiline: {
          padding: `${spacing * 0.5}px 0`,
        },
        inputAdornedStart: {
          paddingLeft: 0,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          height: spacing * 11,
          '&.MuiInputBase-sizeSmall': {
            paddingLeft: spacing * 3,
          },
        },
        paper: {
          borderRadius: '12px',
          boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.13)',
        },
        listbox: {
          '& .MuiAutocomplete-option': {
            padding: `${spacing * 2}px ${spacing * 5}px`,
            minHeight: `${spacing * 6}px`,
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: '#fff',
          },
          '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused': {
            backgroundColor: colors.rose[50],
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: spacing * 5,
          paddingBottom: 0,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: spacing * 5,
          paddingTop: `${spacing * 5}px !important`,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${spacing * 5}px !important`,
          gap: spacing * 3,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 12,
          color: colors.grey[600],
        },
      },
    },
    MuiDateRangePicker: {
      defaultProps: {
        slotProps: {
          fieldRoot: {
            sx: { alignItems: 'start' },
          },
          fieldSeparator: {
            sx: {
              height: 2.5,
              paddingTop: 1.5,
              paddingBottom: 1.5,
            },
          },
        },
      },
    },
  },
};

export const theme = createTheme(themeConfig, deDE);
